import React, { useState, useEffect, useRef } from 'react';
import { PageHeader, Button, Typography, Row, Col } from 'antd';
import { MyBreadcrumb } from 'utils';
import { PrinterOutlined } from '@ant-design/icons';
import {
  handleApiError,
  axiosBase,
  renderInfoBlock,
  shouldShowBlock,
} from 'utils';
import { useReactToPrint } from 'react-to-print';

const { Title } = Typography;

function buildAddress(a1, a2, city, state, zip, country) {
  const lines = [];
  if (a1) lines.push(a1);
  if (a2) lines.push(a2);
  const cityLine = [city, state, zip].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  // Only include country if there are other address parts
  if (country && lines.length > 0) lines.push(country);
  return lines.join('\n');
}

export default function View(props) {
  const { id } = props.match.params;
  const axios = axiosBase.create();
  const [isLoading, setIsLoading] = useState(false);
  const [item, setItem] = useState({});
  const componentRef = useRef(null);

  useEffect(() => {
    console.log('props', props);
    if (id) {
      axios({
        method: 'GET',
        url: '/utility-provider/view/' + id,
      })
        .then((response) => {
          setIsLoading(false);
          if (response.data.item) {
            setItem(response.data.item);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    }
  }, [axios, id, props]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const noticesAddress = buildAddress(
    item.notices_address1,
    item.notices_address2,
    item.notices_city,
    item.notices_state,
    item.notices_zip,
    item.notices_country,
  );

  const billingAddressFormatted = buildAddress(
    item.billing_address1,
    item.billing_address2,
    item.billing_city,
    item.billing_state,
    item.billing_zip,
    item.billing_country,
  );

  const contactAddressFormatted = buildAddress(
    item.provider_contact_address1,
    item.provider_contact_address2,
    item.provider_contact_city,
    item.provider_contact_state,
    item.provider_contact_zip,
    item.provider_contact_country,
  );

  const providerBillingAddressFormatted = buildAddress(
    item.provider_billing_address1,
    item.provider_billing_address2,
    item.provider_billing_city,
    item.provider_billing_state,
    item.provider_billing_zip,
    item.provider_billing_country,
  );

  const showReferral =
    item.referral_partner &&
    item.referral_partner.toString().toUpperCase() !== 'NO';

  const generalInfoItems = [
    'account_number',
    { key: 'Vendor ID', value: item.ap_vendor_id },
    { key: 'Service Code', value: item.ap_service_code },
    'status',
    'auto_pay',
    'payment_timeline',
    'where_invoice_sent',
    {
      key: 'AP Manager',
      value: item.ap_manager ? item.ap_manager.display_name : '',
    },
    'circuit_id_meter_id',
    ...(showReferral
      ? [
          'referral_partner',
          {
            key: 'Referred By',
            value: item.referred_by ? item.referred_by.utility_provider : '',
          },
        ]
      : []),
  ];

  const noticesAddressItems = noticesAddress
    ? [{ key: 'address', value: noticesAddress, hideLabel: true }]
    : [];

  const contractInfoItems = [
    'monthly_fee',
    'contract_start',
    'contract_end',
    'installation_fee',
    { key: 'Signed By', value: item.contract_signedby },
    { key: 'SLA', value: item.sla },
  ];

  const billingAddressItems = [
    'billing_name',
    ...(billingAddressFormatted
      ? [{ key: 'address', value: billingAddressFormatted, hideLabel: true }]
      : []),
    { key: 'Contact', value: item.billing_contact },
    { key: 'Phone', value: item.billing_phone },
    { key: 'Email', value: item.billing_email },
    { key: 'Fax', value: item.billing_fax },
  ];

  const utilityProviderInfoItems = [
    { key: 'Provider', value: item.utility_provider },
    { key: 'Sales Email', value: item.sales_agent_email },
    { key: 'Sales Phone', value: item.sales_agent_phone },
    { key: 'Sales Agent', value: item.sales_agent_name },
    'notes',
  ];

  const contactInfoItems = [
    ...(contactAddressFormatted
      ? [{ key: 'address', value: contactAddressFormatted, hideLabel: true }]
      : []),
    { key: 'Phone', value: item.provider_contact_phone },
    { key: 'Fax', value: item.provider_contact_fax },
  ];

  const supportInfoItems = [
    { key: 'Email', value: item.provider_support_email },
    { key: 'Website', value: item.provider_support_website },
    { key: 'Phone', value: item.provider_support_phone },
  ];

  const providerBillingItems = [
    ...(providerBillingAddressFormatted
      ? [
          {
            key: 'address',
            value: providerBillingAddressFormatted,
            hideLabel: true,
          },
        ]
      : []),
    { key: 'Phone', value: item.provider_billing_phone },
    { key: 'Fax', value: item.provider_billing_fax },
  ];

  const terminationInfoItems = [
    { key: 'Email', value: item.provider_termination_email },
    { key: 'Phone', value: item.provider_termination_phone },
    { key: 'Sales Email', value: item.provider_termination_sales_email },
    { key: 'Sales Phone', value: item.provider_termination_sales_phone },
  ];

  const serviceAreaItems = [
    {
      key: 'Service Area',
      value:
        item.provider_service_area === 'SELECT_STATE'
          ? ''
          : item.provider_service_area,
    },
    { key: 'States', value: item.provider_service_area_states },
  ];

  const notesItems = [{ key: 'Notes', value: item.provider_notes }];

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Utility Providers', path: '/utility-providers' },
          { label: 'View', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title="View"
        subTitle=""
        extra={[
          <Button
            key="1"
            onClick={handlePrint}
            type="primary"
            shape="round"
            icon={<PrinterOutlined />}
          >
            Print
          </Button>,
        ]}
      >
        {isLoading ? 'Loading...' : ''}

        <div ref={componentRef}>
          <div className="descriptionView2-heading">
            <Title level={3}>
              Utility Providers - {item?.utility_provider}
            </Title>
            <Title level={3}>
              Status:{' '}
              {item.status === 'ACTIVE' ? (
                <span className="text-green">Active</span>
              ) : (
                <span className="text-yellow">{item.status}</span>
              )}
            </Title>
            {item.monthly_fee && (
              <Title level={3}>
                Monthly Fee:{' '}
                <span className="text-green">${item.monthly_fee}</span>
              </Title>
            )}
          </div>

          <div style={{ maxWidth: 900, margin: 'auto' }}>
            <Row gutter={[16, 16]}>
              {shouldShowBlock({
                heading: 'General Info',
                source: item,
                items: generalInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'General Info',
                    source: item,
                    items: generalInfoItems,
                  })}
                </Col>
              )}

              {noticesAddressItems.length > 0 && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Notices Address',
                    source: item,
                    items: noticesAddressItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Contract Information',
                source: item,
                items: contractInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Contract Information',
                    source: item,
                    items: contractInfoItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Credentials',
                source: item,
                items: [
                  'primary_on_account',
                  'pin',
                  'security_question',
                  'phone_number',
                  'username',
                  'password',
                  'website',
                  'email',
                ],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Credentials',
                    source: item,
                    items: [
                      'primary_on_account',
                      'pin',
                      'security_question',
                      'phone_number',
                      'username',
                      'password',
                      'website',
                      'email',
                    ],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Location',
                source: item,
                items: ['location_name', 'node_id'],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Location',
                    source: item,
                    items: ['location_name', 'node_id'],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Billing Address',
                source: item,
                items: billingAddressItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Billing Address',
                    source: item,
                    items: billingAddressItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Site Information',
                source: item,
                items: ['service_equipment_location'],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Site Information',
                    source: item,
                    items: ['service_equipment_location'],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Information',
                source: item,
                items: utilityProviderInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Information',
                    source: item,
                    items: utilityProviderInfoItems,
                  })}
                </Col>
              )}

              {contactInfoItems.length > 0 &&
                shouldShowBlock({
                  heading: 'Contact Information',
                  source: item,
                  items: contactInfoItems,
                }) && (
                  <Col xs={24} xl={12}>
                    {renderInfoBlock({
                      heading: 'Contact Information',
                      source: item,
                      items: contactInfoItems,
                    })}
                  </Col>
                )}

              {shouldShowBlock({
                heading: 'Support Information',
                source: item,
                items: supportInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Support Information',
                    source: item,
                    items: supportInfoItems,
                  })}
                </Col>
              )}

              {providerBillingItems.length > 0 &&
                shouldShowBlock({
                  heading: 'Billing Information',
                  source: item,
                  items: providerBillingItems,
                }) && (
                  <Col xs={24} xl={12}>
                    {renderInfoBlock({
                      heading: 'Billing Information',
                      source: item,
                      items: providerBillingItems,
                    })}
                  </Col>
                )}

              {shouldShowBlock({
                heading: 'Termination Information',
                source: item,
                items: terminationInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Termination Information',
                    source: item,
                    items: terminationInfoItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Service Area',
                source: item,
                items: serviceAreaItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Service Area',
                    source: item,
                    items: serviceAreaItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Notes',
                source: item,
                items: notesItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Notes',
                    source: item,
                    items: notesItems,
                  })}
                </Col>
              )}
            </Row>
          </div>
        </div>
      </PageHeader>
    </div>
  );
}
